import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

import { KIND, SINGLETON_ID } from '../lib/types'

/**
 * Lokaler Speicher. Die IndexedDB ist unterwegs die Wahrheit: jede Änderung
 * landet zuerst hier und wird erst danach zum Server geschoben. Ohne Netz
 * funktioniert die App vollständig, nur eben allein.
 *
 * ## Je Reise eine Datenbank
 *
 * Seit die Reise das Objekt ist, an dem die Berechtigung hängt, gibt es den
 * einen Bestand nicht mehr. Jede Reise bekommt deshalb ihre eigene IndexedDB,
 * und jeder Zugriff hier nennt die Reise, die er meint.
 *
 * Getrennte Datenbanken und nicht eine gemeinsame mit einer Spalte "tripId":
 *
 * - Eine Reise kann nicht in die andere laufen. Kein Index, keine Abfrage und
 *   kein vergessenes Prädikat kann Dokumente einer fremden Reise liefern -
 *   die Verbindung führt schlicht woandershin. Bei einer gemeinsamen Tabelle
 *   hänge die Trennung an jeder einzelnen Abfrage, und eine vergessene
 *   genügte, um zwei Reisen zu vermischen. Das ist nicht zu reparieren, weil
 *   hinterher niemand mehr weiss, was wohin gehörte.
 * - Der Abgleichzeiger liegt damit von selbst je Reise richtig. Er steht als
 *   'sync:cursor' im meta-Speicher, und den gibt es jetzt eben je Datenbank.
 *   Ein Zeiger von Reise A kann Reise B nicht erreichen; das ist keine
 *   Namenskonvention, die jemand einhalten muss, sondern eine Eigenschaft des
 *   Aufbaus.
 * - Der Wechsel zwischen zwei Reisen bewegt keine Daten. Beide Bestände
 *   bleiben liegen, samt ihrer noch nicht gesendeten Änderungen; wer
 *   zurückwechselt, findet sie wieder. Ein Umschalten, das den einen Bestand
 *   für den anderen leert, verlöre genau das, was noch niemand hat.
 * - Kein Schema-Umbau am Altbestand: die vorhandene Datenbank 'roadbook' hat
 *   bereits genau diese Form. Sie wird nicht umgeschrieben, sondern einer
 *   Reise zugeordnet (adoptiereAltbestand). Kein Byte wird bewegt, und ein
 *   Abbruch mittendrin kann nichts verlieren.
 *
 * Bezahlt wird das mit dem Abmelden: es muss alle Datenbanken finden, nicht
 * eine leeren. Siehe wipeAlles.
 */

/** Ein Dokument, so wie es lokal liegt. */
export interface LocalDoc {
  kind: string
  id: string
  body: unknown
  /** Zuletzt vom Server bestätigte Revision. 0 heisst: nie synchronisiert. */
  rev: number
  deleted: boolean
  /** 1, solange die lokale Fassung noch nicht beim Server ist. */
  dirty: 0 | 1
  /** Zeitpunkt der letzten lokalen Änderung, in Millisekunden. */
  updatedAt: number
  /**
   * 1, solange dies die unangetastete Startbestückung ist (die vorgeplante
   * Route aus lib/seed.ts). Die erste echte Änderung entfernt die Markierung,
   * denn ab da ist es der Inhalt des Nutzers.
   *
   * Nur eine so markierte Vorlage verliert im Abgleich bedingungslos gegen ein
   * entferntes Dokument mit Vorgeschichte. Ohne die Markierung müsste der
   * Abgleich raten - und verwürfe dabei auch offline geschriebene Inhalte, die
   * den Server nur noch nicht gesehen haben.
   *
   * Zahl statt Wahrheitswert, damit daraus ein Index werden kann, wie bei dirty.
   */
  seed?: 0 | 1
  /**
   * Lokale Änderungsnummer, bei jedem write() um mindestens eins erhöht.
   *
   * Sie dient allein dem Erkennen von Eingaben, die während eines laufenden
   * Pushs entstanden sind. Der Zeitstempel taugt dafür nicht: er hat nur
   * Millisekundenauflösung, und zwei Änderungen im selben Tick sähen gleich
   * aus. Für die Konfliktauflösung bleibt der Zeitstempel zuständig.
   */
  localSeq?: number
}

/** Ein Bild samt Bilddaten, bis es hochgeladen ist. */
export interface LocalBlob {
  id: string
  stageId: string
  blob: Blob
  /** 1, sobald der Server die Bytes hat. */
  uploaded: 0 | 1
  /**
   * 1, wenn der Server die Bytes dauerhaft ablehnt (zu gross, falsches Format).
   * Zahl statt Wahrheitswert, damit daraus ein Index werden kann.
   */
  failed: 0 | 1
  /** Begründung der Ablehnung, wie sie der Server geliefert hat. */
  failedReason?: string
  /**
   * Wie oft der Server das Bild schon mit einem Fehler beantwortet hat.
   *
   * Nur beantwortete Versuche zählen: ohne Netz hat das Bild nichts falsch
   * gemacht. Nach genug Fehlschlägen gilt auch ein 5xx als dauerhaft, sonst
   * versucht das Geraet dasselbe Bild bis in alle Ewigkeit.
   */
  attempts?: number
  /**
   * Revision eines Grabsteins, den der Abgleich übergangen hat, weil die Bytes
   * hier noch nicht hochgeladen waren.
   *
   * Der Cursor läuft trotzdem weiter, der Server liefert den Grabstein also nie
   * wieder. Solange der Upload noch aussteht, ist das richtig: er stellt das
   * Foto wieder her. Wird er aber endgültig aufgegeben, ist die Notiz die
   * einzige Spur, an der sich das Geraet noch mit dem Server in Deckung bringen
   * kann - sonst bleibt das Foto hier für immer stehen, während alle anderen
   * es geloescht haben.
   */
  remoteDeletedRev?: number
  createdAt: number
}

interface RoadbookDB extends DBSchema {
  docs: {
    key: [string, string]
    value: LocalDoc
    indexes: { 'by-kind': string; 'by-dirty': number }
  }
  blobs: {
    key: string
    value: LocalBlob
    indexes: { 'by-uploaded': number; 'by-failed': number }
  }
  meta: {
    key: string
    value: unknown
  }
}

/**
 * Der Name der Datenbank aus der Zeit vor dem Reisemodell.
 *
 * Sie enthält auf jeder laufenden Installation den gesamten Bestand und wird
 * niemals geloescht oder umkopiert, sondern einer Reise zugeordnet.
 */
const ALTBESTAND_DB = 'roadbook'

/** Vor der Reise-ID, damit fremde Datenbanken derselben Herkunft erkennbar sind. */
const DB_PRAEFIX = 'roadbook-'

const DB_VERSION = 1

/**
 * Steht im meta-Speicher und sagt, zu welcher Reise diese Datenbank gehoert.
 *
 * Die Zuordnung steht damit in der Datenbank selbst und nicht nur in einer
 * Liste daneben: die Liste im localStorage ist ein Zwischenspeicher, den ein
 * Nutzer jederzeit leeren kann, und nur der Eintrag hier überlebt das.
 */
const TRIP_KEY = 'trip:id'

/** Zuordnung Reise-ID zu Datenbankname, als Abkürzung für den naechsten Start. */
const NAMEN_KEY = 'roadbook:db-namen'

/**
 * Der Abgleichzeiger: bis hierher hat dieses Geraet den Änderungsstrom gesehen.
 *
 * Er steht im meta-Speicher der Reise und damit je Reise für sich. Das ist die
 * gefährlichste Stelle des ganzen Umbaus: zöge Reise B mit dem Zeiger von
 * Reise A los, übersprunge sie alles, was darunter liegt, und der Nutzer sähe
 * eine halb leere Reise, ohne dass irgendwo ein Fehler gemeldet würde.
 */
export const CURSOR_KEY = 'sync:cursor'

/** Zeitpunkt des letzten erfolgreichen Abgleichs, ebenfalls je Reise. */
export const LAST_SYNC_KEY = 'sync:lastSync'

/**
 * Der Bestand, der noch keiner Reise zugeordnet ist.
 *
 * Auf einer Installation aus der Zeit vor dem Reisemodell liegen die Daten in
 * der Datenbank 'roadbook', und welche Reise das auf dem Server ist, weiss
 * allein der Server (ApiClient.legacyTripId). Bis die Zuordnung geglückt ist,
 * lässt sich mit diesem Bestand trotzdem arbeiten - offline zu stehen darf
 * nicht heissen, das eigene Tagebuch nicht mehr aufschlagen zu können.
 * Abgeglichen wird dabei nicht: wohin, weiss ja niemand.
 */
export const ALTBESTAND = ''

/** Offene Verbindungen je Datenbankname. */
const verbindungen = new Map<string, Promise<IDBPDatabase<RoadbookDB>>>()

/**
 * Verbindungen, deren Reisevermerk bereits geprueft ist.
 *
 * Getrennt von den offenen Verbindungen, weil die Prüfung an der Reise hängt
 * und das Öffnen am Namen: alleBestaende() sieht in jede Datenbank hinein,
 * ohne vorher zu wissen, zu welcher Reise sie gehoert, und darf die Prüfung
 * damit nicht überspringen.
 */
const gepruefteVerbindungen = new Map<string, Promise<IDBPDatabase<RoadbookDB>>>()

function namen(): Record<string, string> {
  try {
    const roh = localStorage.getItem(NAMEN_KEY)
    if (!roh) return {}
    const wert: unknown = JSON.parse(roh)
    return wert && typeof wert === 'object' ? (wert as Record<string, string>) : {}
  } catch {
    // Kein localStorage (privater Modus) oder kaputter Eintrag: dann eben ohne.
    return {}
  }
}

function merkeNamen(tripId: string, name: string): void {
  try {
    localStorage.setItem(NAMEN_KEY, JSON.stringify({ ...namen(), [tripId]: name }))
  } catch {
    // Nicht speicherbar ist kein Grund, den Zugriff zu verweigern: der Eintrag
    // im meta-Speicher der Datenbank bleibt die Wahrheit.
  }
}

/** Der Datenbankname einer Reise. */
export function dbNameFuer(tripId: string): string {
  if (tripId === ALTBESTAND) return ALTBESTAND_DB
  return namen()[tripId] ?? DB_PRAEFIX + tripId
}

/**
 * Öffnet die Datenbank einer Reise und stellt sicher, dass es ihre ist.
 *
 * Die Prüfung auf TRIP_KEY ist der Riegel gegen die eine Verwechslung, die
 * nicht mehr gutzumachen wäre: eine Datenbank, die zu Reise A gehoert, unter
 * dem Namen von Reise B zu beschreiben. Das kann nur ein Fehler in der
 * Zuordnung sein, und dann ist ein Abbruch das einzig Richtige - weiterlaufen
 * hiesse, zwei Reisen ineinanderzuschreiben.
 */
function oeffneNachName(name: string): Promise<IDBPDatabase<RoadbookDB>> {
  let offen = verbindungen.get(name)
  if (offen) return offen

  offen = openDB<RoadbookDB>(name, DB_VERSION, {
    upgrade(db) {
      const docs = db.createObjectStore('docs', { keyPath: ['kind', 'id'] })
      docs.createIndex('by-kind', 'kind')
      docs.createIndex('by-dirty', 'dirty')

      const blobs = db.createObjectStore('blobs', { keyPath: 'id' })
      blobs.createIndex('by-uploaded', 'uploaded')
      blobs.createIndex('by-failed', 'failed')

      db.createObjectStore('meta')
    },
  })

  verbindungen.set(name, offen)
  // Ein gescheiterter Aufbau darf nicht als Verbindung liegen bleiben, sonst
  // scheitert jeder weitere Zugriff mit demselben alten Fehler.
  offen.catch(() => verbindungen.delete(name))
  return offen
}

function db(tripId: string): Promise<IDBPDatabase<RoadbookDB>> {
  const name = dbNameFuer(tripId)
  const geprueft = gepruefteVerbindungen.get(name)
  if (geprueft) return geprueft

  const offen = (async () => {
    // Den Altbestand gibt es oder es gibt ihn nicht - angelegt wird er nie.
    // Ein open() legt die Datenbank an, und eine leere, frisch entstandene
    // 'roadbook' sähe beim naechsten Start aus wie ein Bestand aus der Zeit
    // vor dem Reisemodell und würde einer Reise zugeschlagen.
    if (tripId === ALTBESTAND && !(await alleDbNamen()).includes(ALTBESTAND_DB)) {
      throw new Error('Es liegt kein Altbestand auf diesem Gerät')
    }

    const database = await oeffneNachName(name)

    const vermerkt = (await database.get('meta', TRIP_KEY)) as string | undefined
    if (tripId === ALTBESTAND) {
      // Der Altbestand ist genau so lange der Altbestand, wie er keiner Reise
      // gehoert. Steht die Zuordnung schon, ist der Aufrufer stehen geblieben.
      if (vermerkt !== undefined) {
        throw new Error(`Der Altbestand gehört bereits zur Reise ${vermerkt}`)
      }
    } else if (vermerkt === undefined) {
      await database.put('meta', tripId, TRIP_KEY)
    } else if (vermerkt !== tripId) {
      throw new Error(`Datenbank ${name} gehört zur Reise ${vermerkt}, nicht zu ${tripId}`)
    }

    // Bei jedem Öffnen und nicht nur beim ersten. Der Eintrag im localStorage
    // ist der einzige Weg, eine Datenbank wiederzufinden, wenn der Browser
    // indexedDB.databases() nicht kennt - und er ist ein Zwischenspeicher, den
    // ein Nutzer jederzeit leeren kann. Wurde er nur beim Anlegen geschrieben,
    // fand das Abmelden danach nichts mehr: wipeAlles hätte den Bestand stehen
    // lassen, und die Rückfrage davor hätte null offene Änderungen gezählt,
    // während das Löschen sie vermeintlich mitnimmt. Auffrischen kostet einen
    // Schreibvorgang je geöffneter Datenbank.
    //
    // Auch für den Altbestand, dessen Reise-ID leer ist: dbNameFuer beantwortet
    // die leere ID ohnehin ohne die Liste, der Eintrag dient allein dazu, dass
    // alleDbNamen die Datenbank 'roadbook' überhaupt aufzählt.
    merkeNamen(tripId, name)
    return database
  })()

  gepruefteVerbindungen.set(name, offen)
  offen.catch(() => gepruefteVerbindungen.delete(name))
  return offen
}

/**
 * Ob auf diesem Geraet ein Bestand ohne Reise-ID liegt.
 *
 * Fragt die Liste der Datenbanken ab und öffnet dabei nichts: ein open()
 * legte die Datenbank an, und danach sähe jede Neuinstallation wie eine
 * Installation mit Altbestand aus.
 *
 * Kennt der Browser indexedDB.databases() nicht, lautet die Antwort "nein".
 * Das ist die vorsichtige Richtung: die Zuordnung unterbleibt, der Bestand
 * bleibt liegen, und der Nutzer arbeitet in der Reise weiter, die er wählt.
 * Ein falsches "ja" würde dagegen eine leere Datenbank anlegen und sie einer
 * Reise zuschlagen.
 */
export async function altbestandVorhanden(): Promise<boolean> {
  const liste = await alleDbNamen()
  if (!liste.includes(ALTBESTAND_DB)) return false
  const database = await db(ALTBESTAND).catch(() => null)
  // Der Zugriff wirft, sobald der Altbestand schon zugeordnet ist. Dann ist er
  // keiner mehr.
  return database !== null
}

/**
 * Ordnet den Altbestand einer Reise zu.
 *
 * Es wird nichts kopiert und nichts bewegt: die vorhandene Datenbank bekommt
 * einen Vermerk und ist ab jetzt die Datenbank dieser Reise. Damit überleben
 * auch die noch nicht gesendeten Änderungen die Umstellung - sie sind der
 * einzige Bestand, den es sonst nirgends gibt.
 *
 * Prüfen und Schreiben liegen in einer Transaktion, und das ist hier der
 * eigentliche Riegel. Zwei Zuordnungen können gleichzeitig laufen - die eine
 * aus dem Erststart, sobald der Server wieder erreichbar ist, die andere aus
 * dem Anlegen einer Reise -, und getrennt gelesen sähen beide "gehört noch
 * niemandem" und schrieben nacheinander verschiedene Reise-IDs. Der letzte
 * gewänne in der Datenbank, im localStorage stünden aber beide Reisen auf
 * dieselbe Datenbank: die Verliererin liesse sich danach nie wieder öffnen, und
 * die App bliebe bei "Lokaler Bestand wird gelesen …" stehen.
 *
 * Dieselbe Reise zweimal zu sehen ist dabei kein Fehler: zwei gleichzeitige
 * Anläufe derselben Zuordnung sind der Normalfall, wenn ein Effekt zweimal
 * läuft. Ein späterer Anlauf scheitert dagegen schon eine Zeile vorher, in
 * db(ALTBESTAND) - nach der ersten Zuordnung gibt es keinen Altbestand mehr,
 * und wer danach noch einmal fragt, ist stehen geblieben.
 *
 * @param cursorZuruecksetzen für eine frisch angelegte Reise. Der gemerkte
 *   Zeiger zeigt dann in den Änderungsstrom einer anderen Reise, und dort
 *   steht er zu weit vorn: der erste Pull übersprang alles darunter. Bei der
 *   vom Server aufgelösten Reise gilt das Gegenteil - dort ist es der Strom,
 *   aus dem der Bestand stammt, und der Zeiger bleibt.
 */
export async function adoptiereAltbestand(
  tripId: string,
  cursorZuruecksetzen = false,
): Promise<void> {
  if (tripId === ALTBESTAND) throw new Error('Ohne Reise-ID gibt es nichts zuzuordnen')

  const database = await db(ALTBESTAND)
  const tx = database.transaction('meta', 'readwrite')
  const vermerkt = (await tx.store.get(TRIP_KEY)) as string | undefined
  if (vermerkt !== undefined && vermerkt !== tripId) {
    // Nicht abbrechen, sondern nichts schreiben: die Transaktion schliesst von
    // selbst, und ein abort() liesse zusätzlich tx.done abgelehnt zurück, auf
    // das hier niemand mehr wartet.
    throw new Error(`Der Altbestand gehört bereits zur Reise ${vermerkt}, nicht zu ${tripId}`)
  }
  await tx.store.put(tripId, TRIP_KEY)
  if (cursorZuruecksetzen) await tx.store.delete(CURSOR_KEY)
  await tx.done

  merkeNamen(tripId, ALTBESTAND_DB)
  // Die Verbindung liegt unter dem Namen der Datenbank, nicht unter der
  // Reise-ID: sie bleibt gültig und wird ab jetzt über die Reise gefunden.
  // Die einmal geprueft abgelegte Zusage "gehoert keiner Reise" gilt dagegen
  // nicht mehr und muss weg, sonst ginge der Altbestand weiterhin als solcher
  // durch.
  gepruefteVerbindungen.delete(ALTBESTAND_DB)
}

/** Alle Datenbanken dieser Anwendung auf diesem Geraet. */
async function alleDbNamen(): Promise<string[]> {
  const gemerkt = Object.values(namen())
  try {
    const liste = await indexedDB.databases()
    const gefunden = liste.map((eintrag) => eintrag.name ?? '').filter((name) => name !== '')
    return [...new Set([...gefunden, ...gemerkt])].filter(gehoertUns)
  } catch {
    return [...new Set(gemerkt)].filter(gehoertUns)
  }
}

function gehoertUns(name: string): boolean {
  return name === ALTBESTAND_DB || name.startsWith(DB_PRAEFIX)
}

/**
 * Alle Bestände auf diesem Geraet, als Reise-IDs; ein noch nicht zugeordneter
 * Altbestand erscheint als ALTBESTAND.
 *
 * Gebraucht wird das, wo eine Aussage über das ganze Geraet fällig ist: was
 * das Abmelden vernichten würde, steht nicht nur in der offenen Reise. Eine
 * Rückfrage, die nur die offene zählt, verschwiege genau die Änderungen, an
 * die im Moment niemand denkt.
 */
export async function alleBestaende(): Promise<string[]> {
  const ids: string[] = []
  for (const name of await alleDbNamen()) {
    try {
      const database = await oeffneNachName(name)
      const vermerkt = (await database.get('meta', TRIP_KEY)) as string | undefined
      if (vermerkt !== undefined) ids.push(vermerkt)
      else if (name === ALTBESTAND_DB) ids.push(ALTBESTAND)
    } catch (err) {
      console.warn('Bestand liess sich nicht lesen', name, err)
    }
  }
  return [...new Set(ids)]
}

/**
 * Das Reisedokument jedes Bestands auf diesem Gerät, mit seiner Reise-ID.
 *
 * Gebraucht wird das für den Fuhrpark: die Fahrzeuge, die auf diesem Gerät
 * schon einmal eingetragen wurden, stehen in den Reisen, in denen sie gefahren
 * werden - eine zweite Ablage daneben gibt es ausdrücklich nicht (siehe
 * fuhrpark in lib/vehicle.ts).
 *
 * Gelesen wird über den Datenbanknamen und nicht über die Reise-ID, genau wie
 * in alleBestaende: die Zuordnung Reise → Datenbankname liegt im localStorage
 * und kann fehlen, der Vermerk in der Datenbank selbst nie. Ein Bestand, der
 * sich nicht lesen lässt, fällt für sich heraus - für eine Liste von
 * Vorschlägen ist das genug, und der Rest der App hängt nicht daran.
 *
 * Der noch nicht zugeordnete Altbestand ist mit dabei, unter ALTBESTAND: sein
 * Fahrzeug ist so gut wie jedes andere, und dass die Reise dazu noch keinen
 * Namen auf dem Server hat, ändert daran nichts.
 */
export async function alleReisedokumente(): Promise<{ tripId: string; body: unknown }[]> {
  const out: { tripId: string; body: unknown }[] = []
  for (const name of await alleDbNamen()) {
    try {
      const database = await oeffneNachName(name)
      const vermerkt = (await database.get('meta', TRIP_KEY)) as string | undefined
      if (vermerkt === undefined && name !== ALTBESTAND_DB) continue
      const doc = await database.get('docs', [KIND.trip, SINGLETON_ID.trip])
      if (!doc || doc.deleted) continue
      out.push({ tripId: vermerkt ?? ALTBESTAND, body: doc.body })
    } catch (err) {
      console.warn('Reisedokument liess sich nicht lesen', name, err)
    }
  }
  return out
}

/** Alle Dokumente einer Reise, auch die gelöschten. Die Auswertung passiert im Aufrufer. */
export async function allDocs(tripId: string): Promise<LocalDoc[]> {
  return (await db(tripId)).getAll('docs')
}

export async function getDoc(
  tripId: string,
  kind: string,
  id: string,
): Promise<LocalDoc | undefined> {
  return (await db(tripId)).get('docs', [kind, id])
}

/** Schreibt ein Dokument unverändert, ohne dirty oder rev anzufassen. */
export async function putDoc(tripId: string, doc: LocalDoc): Promise<void> {
  await (await db(tripId)).put('docs', doc)
}

/**
 * Schreibt eine lokale Änderung und hebt dabei die Revision auf den Stand an,
 * den die Datenbank bereits kennt.
 *
 * Der Aufrufer bildet seine Fassung aus dem React-Zustand, und der kann in der
 * Revision hinterherhinken: die Vorlage aus dem Erststart trägt rev 0, während
 * der Abgleich die echte Fassung mit ihrer Vorgeschichte längst hier abgelegt
 * hat. Ginge diese 0 in die Datenbank, kollidierte der nächste Push, und der
 * Konflikt entschiede nach Zeitstempel zugunsten der Vorlage - die echte Reise
 * wäre auf allen Geräten weg.
 *
 * Die Revision nur anzuheben und nie zu senken ist auch sonst richtig: eine
 * niedrigere Basis als die gespeicherte ist immer eine veraltete Basis. Der
 * Inhalt bleibt dabei der lokale, genau wie bei einem gewonnenen Konflikt in
 * mergeRemote.
 *
 * Lesen und Schreiben liegen in einer Transaktion, sonst fällt ein Schreiben
 * des Abgleichs genau dazwischen.
 *
 * @returns die Fassung, die tatsächlich gespeichert wurde.
 */
export async function putLocalChange(tripId: string, doc: LocalDoc): Promise<LocalDoc> {
  const tx = (await db(tripId)).transaction('docs', 'readwrite')
  const stored = await tx.store.get([doc.kind, doc.id])
  const effektiv = stored && stored.rev > doc.rev ? { ...doc, rev: stored.rev } : doc
  await tx.store.put(effektiv)
  await tx.done
  return effektiv
}

export async function putDocs(tripId: string, docs: LocalDoc[]): Promise<void> {
  if (docs.length === 0) return
  const tx = (await db(tripId)).transaction('docs', 'readwrite')
  await Promise.all([...docs.map((d) => tx.store.put(d)), tx.done])
}

/** Dokumente, die noch zum Server müssen. */
export async function dirtyDocs(tripId: string): Promise<LocalDoc[]> {
  return (await db(tripId)).getAllFromIndex('docs', 'by-dirty', 1)
}

export async function getBlob(tripId: string, id: string): Promise<LocalBlob | undefined> {
  return (await db(tripId)).get('blobs', id)
}

export async function putBlob(tripId: string, blob: LocalBlob): Promise<void> {
  await (await db(tripId)).put('blobs', blob)
}

export async function deleteBlob(tripId: string, id: string): Promise<void> {
  await (await db(tripId)).delete('blobs', id)
}

/**
 * Bilder, deren Bytes der Server noch nicht hat und noch annehmen kann.
 *
 * Dauerhaft abgelehnte Bilder bleiben absichtlich draussen: sie würden sonst
 * bei jedem Durchlauf erneut scheitern und den ganzen Abgleich aufhalten.
 */
export async function pendingBlobs(tripId: string): Promise<LocalBlob[]> {
  const all = await (await db(tripId)).getAllFromIndex('blobs', 'by-uploaded', 0)
  return all.filter((b) => b.failed !== 1)
}

/** Bilder, die der Server dauerhaft abgelehnt hat. */
export async function failedBlobs(tripId: string): Promise<LocalBlob[]> {
  return (await db(tripId)).getAllFromIndex('blobs', 'by-failed', 1)
}

/**
 * Der Stand, den ein abgeschlossener Foto-Upload hinterlässt.
 *
 * Reine Funktion: die Entscheidung soll prüfbar sein, das Schreiben passiert
 * in finishPhotoUpload.
 *
 * @param current das Fotodokument, wie es gerade in der Datenbank liegt.
 * @param uploaded das Dokument, wie es der Server nach dem Upload liefert.
 * @param now Zeitstempel für einen eventuellen Grabstein.
 */
export function photoAfterUpload(
  current: LocalDoc | undefined,
  uploaded: LocalDoc,
  now: number,
): { doc: LocalDoc; keepBytes: boolean } {
  // Während des Uploads geloescht: das Foto ist jetzt beim Server angekommen
  // und muss dort wieder weg. Die eben erhaltene Revision ist die Basis, auf
  // der der nächste Push das Löschen anwendet.
  if (current?.deleted) {
    return {
      doc: { ...current, body: {}, rev: uploaded.rev, deleted: true, dirty: 1, updatedAt: now },
      keepBytes: false,
    }
  }
  // Die Änderungsnummer der lokalen Fassung geht mit: sie sagt aus, welche
  // Schreibgeneration in diesem Eintrag steckt, und das ändert der Upload
  // nicht. Ohne sie hält mergeSnapshot den Platzhalter aus addPhoto dauerhaft
  // für neuer als die Fassung des Servers.
  return { doc: { ...uploaded, localSeq: current?.localSeq }, keepBytes: true }
}

/**
 * Der Grabstein für ein gelöschtes Foto.
 *
 * Reine Funktion: die Entscheidung soll prüfbar sein, das Schreiben passiert
 * in deletePhotoDoc.
 *
 * @param current das Fotodokument, wie es gerade in der Datenbank liegt.
 * @param localSeq die neue lokale Änderungsnummer.
 */
export function photoAfterDelete(
  kind: string,
  id: string,
  current: LocalDoc | undefined,
  now: number,
  localSeq: number,
): LocalDoc {
  const rev = current?.rev ?? 0
  return {
    kind,
    id,
    body: {},
    rev,
    deleted: true,
    // Revision 0 heisst: die Bytes waren nie beim Server, es gibt dort nichts zu
    // loeschen. Ein Grabstein würde nur ins Leere gepusht.
    dirty: rev === 0 ? 0 : 1,
    updatedAt: now,
    // Auch das Löschen ist eine lokale Änderung: ohne neue Nummer hälte ein
    // gleichzeitig laufender Push den Grabstein für schon gesendet.
    localSeq,
  }
}

/**
 * Löscht ein Foto: Grabstein schreiben und die Bilddaten wegräumen.
 *
 * Lesen und Schreiben liegen in einer Transaktion über beide Speicher, spiegel-
 * bildlich zu finishPhotoUpload. Sonst passt genau diese Reihenfolge dazwischen:
 * hier wird die Revision 0 gelesen, dann committet der Upload Dokument und Blob,
 * dann schreibt der Grabstein seine 0 und gilt als nicht schmutzig. Er ginge nie
 * zum Server, das Foto bliebe dort liegen, und der nächste Pull machte es
 * lokal wieder sichtbar.
 *
 * @param nextSeq liefert die nächste lokale Änderungsnummer. Wird innerhalb
 *   der Transaktion gerufen und muss deshalb synchron sein.
 * @returns der geschriebene Grabstein.
 */
export async function deletePhotoDoc(
  tripId: string,
  kind: string,
  id: string,
  now: number,
  nextSeq: (previous?: number) => number,
): Promise<LocalDoc> {
  const tx = (await db(tripId)).transaction(['docs', 'blobs'], 'readwrite')
  const docs = tx.objectStore('docs')

  const current = await docs.get([kind, id])
  const doc = photoAfterDelete(kind, id, current, now, nextSeq(current?.localSeq))

  await docs.put(doc)
  await tx.objectStore('blobs').delete(id)
  await tx.done

  return doc
}

/**
 * Schreibt das Ergebnis eines Foto-Uploads: das Dokument, aber nur wenn es
 * nicht inzwischen geloescht wurde, sonst einen Grabstein mit der neuen
 * Revision.
 *
 * Lesen und Schreiben liegen bewusst in einer Transaktion über beide Speicher.
 * Fällt der Grabstein aus deletePhoto genau dazwischen, überschriebe der
 * Upload ihn und legte die Bilddaten neu an: das Foto wäre wieder da, während
 * die Oberfläche es geloescht zeigt.
 *
 * @returns ob das Foto zwischenzeitlich geloescht wurde.
 */
export async function finishPhotoUpload(
  tripId: string,
  blob: LocalBlob,
  uploaded: LocalDoc,
  now: number,
): Promise<{ deleted: boolean }> {
  const tx = (await db(tripId)).transaction(['docs', 'blobs'], 'readwrite')
  const docs = tx.objectStore('docs')
  const blobs = tx.objectStore('blobs')

  const current = await docs.get([uploaded.kind, uploaded.id])
  const { doc, keepBytes } = photoAfterUpload(current, uploaded, now)

  await docs.put(doc)
  if (keepBytes) await blobs.put({ ...blob, uploaded: 1 })
  else await blobs.delete(blob.id)
  await tx.done

  return { deleted: !keepBytes }
}

export async function getMeta<T>(tripId: string, key: string): Promise<T | undefined> {
  return (await db(tripId)).get('meta', key) as Promise<T | undefined>
}

export async function setMeta(tripId: string, key: string, value: unknown): Promise<void> {
  await (await db(tripId)).put('meta', value, key)
}

/**
 * Grabsteine für alles, was ein Import zurücklässt.
 *
 * Ein Import ersetzt den Bestand, aber nur den lokalen: was der Server schon
 * kennt und im Bundle fehlt, käme beim naechsten Pull ungefragt zurück und
 * vermischte zwei Reisen. Für jedes solche Dokument entsteht hier ein
 * Löscheintrag, der beim naechsten Push auch serverseitig aufräumt.
 *
 * Reine Funktion: sie liest nichts und schreibt nichts, damit sie prüfbar ist.
 *
 * @param existing der lokale Bestand vor dem Import.
 * @param bundleIds je Dokumentart die IDs, die das Bundle mitbringt. Arten, die
 *   hier fehlen, bleiben unangetastet (trip und checks sind Singletons und
 *   werden ohnehin überschrieben).
 * @param now Zeitstempel der Grabsteine.
 */
export function tombstonesForImport(
  existing: LocalDoc[],
  bundleIds: Record<string, ReadonlySet<string>>,
  now: number,
): LocalDoc[] {
  const out: LocalDoc[] = []
  for (const doc of existing) {
    const keep = bundleIds[doc.kind]
    if (!keep || keep.has(doc.id)) continue
    // Schon geloescht: entweder liegt der Grabstein noch als Änderung an, dann
    // holt ihn der Aufrufer über den regulären Weg, oder er ist erledigt.
    if (doc.deleted) continue
    // Revision 0 heisst: der Server hat das Dokument nie gesehen. Ein Grabstein
    // ohne Gegenstück gäbe nur einen sinnlosen Push.
    if (doc.rev === 0) continue
    out.push({
      kind: doc.kind,
      id: doc.id,
      body: {},
      // Die bisherige Revision ist die Basis, auf die der Server das Löschen
      // anwendet. Mit 0 liefe der Push in einen Konflikt.
      rev: doc.rev,
      deleted: true,
      dirty: 1,
      updatedAt: now,
    })
  }
  return out
}

/**
 * Leert den Bestand einer Reise. Gebraucht wird das vom Import, der den
 * gesamten Bestand ersetzt.
 *
 * Die Zuordnung zur Reise überlebt: sie steht im meta-Speicher, und ohne sie
 * wäre die Datenbank hinterher eine namenlose Hülle, die beim naechsten
 * Öffnen als Altbestand durchginge.
 */
export async function wipe(tripId: string): Promise<void> {
  const database = await db(tripId)
  const vermerkt = (await database.get('meta', TRIP_KEY)) as string | undefined

  const tx = database.transaction(['docs', 'blobs', 'meta'], 'readwrite')
  const meta = tx.objectStore('meta')
  // Die Reihenfolge der Aufträge ist die Reihenfolge ihrer Ausführung: der
  // Vermerk wird nach dem Leeren geschrieben, obwohl beides zusammen abgewartet
  // wird.
  const arbeit: Promise<unknown>[] = [
    tx.objectStore('docs').clear(),
    tx.objectStore('blobs').clear(),
    meta.clear(),
  ]
  if (vermerkt !== undefined) arbeit.push(meta.put(vermerkt, TRIP_KEY))
  await Promise.all([...arbeit, tx.done])
}

/**
 * Löscht jeden lokalen Bestand auf diesem Geraet, gleich zu welcher Reise er
 * gehoert. Wird beim Abmelden gebraucht: auf einem geteilten Geraet darf nach
 * dem Logout nichts zurückbleiben - auch nicht die Reise, die gerade nicht
 * offen war.
 *
 * Nicht von ausserhalb der Datenschicht rufen, sondern wipeForSignOut aus dem
 * Roadbook-Kontext. Für sich allein räumt diese Funktion die Datenbank unter
 * einem laufenden Abgleich weg: der liest danach den Cursor neu, findet die 0
 * und holt den gesamten Bestand des eben abgemeldeten Nutzers zurück.
 *
 * Gelöscht wird die ganze Datenbank und nicht ihr Inhalt. Sonst blieben leere
 * Hüllen mit ihrem Reisevermerk stehen, und die nächste Anmeldung auf
 * demselben Geraet fände eine Landkarte der Reisen des Vorgängers vor.
 */
export async function wipeAlles(): Promise<void> {
  const namenListe = await alleDbNamen()

  // Erst schliessen, dann loeschen: eine offene Verbindung lässt
  // deleteDatabase blockieren, und der Aufrufer wartet dann ewig.
  for (const [, offen] of verbindungen) {
    await offen.then(
      (database) => database.close(),
      () => undefined,
    )
  }
  verbindungen.clear()
  gepruefteVerbindungen.clear()

  try {
    localStorage.removeItem(NAMEN_KEY)
  } catch {
    // Kein localStorage: dann gab es auch nichts zu merken.
  }

  await Promise.all(
    namenListe.map(
      (name) =>
        new Promise<void>((fertig) => {
          const anfrage = indexedDB.deleteDatabase(name)
          anfrage.onsuccess = () => fertig()
          // Ein Fehlschlag darf das Abmelden nicht aufhalten: der Rest muss
          // trotzdem weg, und die Anmeldeschicht wartet auf dieses Versprechen.
          anfrage.onerror = () => {
            console.error('Datenbank liess sich nicht löschen', name)
            fertig()
          }
          // Blockiert heisst: eine andere Lasche hält noch eine Verbindung. Der
          // Auftrag bleibt trotzdem stehen und wird ausgeführt, sobald sie
          // fällt - der Browser kündigt ihn den offenen Verbindungen als
          // versionchange an. Hier wird nur nicht mehr darauf gewartet, denn
          // sonst hinge das Abmelden an einem Fenster, an das gerade niemand
          // denkt. Bis dahin liegt der Bestand dort weiter; ein zweiter Anlauf
          // beim nächsten Start wäre eine Zusage, die niemand einlöst.
          anfrage.onblocked = () => {
            console.error(
              'Datenbank blockiert, sie wird gelöscht, sobald die andere Lasche zu ist',
              name,
            )
            fertig()
          }
        }),
    ),
  )
}
